import i18n from '@/i18n/i18n'
import CouponService from '@/services/admin/CouponService.js'
import { build_query, errorMessage } from '@/utilities/helpers.js'

const { t } = i18n.global

export const allowedFilters = ['code', 'description', 'redemption_limit', 'tckn']
export const allowedIncludes = []
export const allowedSorts = [
  'code',
  'description',
  'redemption_limit',
  'tckn',
  'collection_id',
  'started_at',
  'expired_at',
  'created_at'
]
export const allowedAppends = []

export const namespaced = true

export const state = {
  coupons: null,
  coupon: {},
  meta: null
}

export const mutations = {
  SET_COUPONS(state, coupons) {
    state.coupons = coupons.data
    state.meta = coupons.meta
  },
  SET_COUPON(state, coupon) {
    state.coupon = coupon
  },
  ADD_COUPON(state, coupon) {
    state.coupons.push(coupon)
  },
  SET_VALUE(state, parameters) {
    state[parameters.type] = parameters.value
  }
}

export const actions = {
  getCoupons({ commit, dispatch }, query) {
    let newQuery = build_query(
      query,
      allowedFilters,
      allowedIncludes,
      Object.keys(query).length !== 0 ? allowedSorts : '-created_at',
      allowedAppends
    )

    return new Promise((resolve, reject) => {
      CouponService.getCoupons(newQuery)
        .then(response => {
          commit('SET_COUPONS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.coupon.coupons')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getCoupon({ commit, state, getters }, id) {
    return new Promise(resolve => {
      CouponService.getCoupon(id).then(response => {
        commit('SET_COUPON', response.data)
        resolve(response.data)
      })
    })
  },
  createCoupon({ dispatch, commit, state }, coupon) {
    return new Promise((resolve, reject) => {
      CouponService.createCoupon(coupon)
        .then(response => {
          if (state.coupons && response.data) {
            commit('ADD_COUPON', response.data)
          }

          const notification = {
            type: 'success',
            message: t('store.shared.created', {
              element: t('store.admin.coupon.new_coupon')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.create_error', {
                element: t('store.admin.coupon.coupon')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteCoupon({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      CouponService.deleteCoupon(params.id)
        .then(response => {
          commit('SET_COUPONS', response)
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.coupon.coupon')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.coupon.coupon')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  setValue({ commit }, parameters) {
    commit('SET_VALUE', parameters)
  },
  createDiscountRange({ dispatch, commit, state }, { coupon, discountRange }) {
    return new Promise((resolve, reject) => {
      CouponService.createDiscountRange({ coupon, discountRange })
        .then(response => {
          commit('SET_COUPON', response.data)
          resolve(response.data)
          const notification = {
            type: 'success',
            message: t('store.shared.created', {
              element: t('store.admin.coupon.new_discount_range')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.create_error', {
                element: t('store.admin.coupon.discount_range')
              }) +
              ': ' +
              errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteDiscountRange({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      CouponService.deleteDiscountRange(params.couponId, params.discountRangeId)
        .then(response => {
          commit('SET_COUPON', response.data)
          resolve(response.data)
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.coupon.discount_range')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.coupon.discount_range')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createRequirement({ dispatch, commit, state }, { coupon, requirement }) {
    return new Promise((resolve, reject) => {
      CouponService.createRequirement({
        coupon,
        requirement
      })
        .then(response => {
          commit('SET_COUPON', response.data)
          resolve(response.data)
          const notification = {
            type: 'success',
            message: t('store.shared.requirement_create', {
              element: t('store.admin.coupon.discount_range')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.requirement_create_error', {
                element: t('store.admin.coupon.coupon')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteRequirement({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      CouponService.deleteRequirement(params.couponId, params.requirementId)
        .then(response => {
          commit('SET_COUPON', response.data)
          resolve(response.data)
          const notification = {
            type: 'success',
            message: t('store.shared.requirement_deleted', {
              element: t('store.admin.coupon.coupon')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.requirement_delete_error', {
                element: t('store.admin.coupon.coupon')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  importTCKN({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      CouponService.importTCKN(form)
        .then(response => {
          resolve(response.data)
          const notification = {
            type: 'success',
            message: t('store.admin.coupon.nin_import_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
